import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import {
  fetchNitrateNitriteCompliance,
  fetchSystem,
  fetchSystemDocuments,
  refreshSystem,
  refreshSystemDocuments,
} from "@/api/endpoints";
import { AlertsBanner } from "@/components/cards/AlertsBanner";
import { CardError, CardSkeleton } from "@/components/cards/CardSkeleton";
import { DocumentsCard } from "@/components/cards/DocumentsCard";
import { LSLICard } from "@/components/cards/LSLICard";
import { MonitoringScheduleCard } from "@/components/cards/MonitoringScheduleCard";
import { NitrateNitriteCard } from "@/components/cards/NitrateNitriteCard";
import { PlantCard } from "@/components/cards/PlantCard";
import { StorageCard } from "@/components/cards/StorageCard";
import { SystemAttributesCard } from "@/components/cards/SystemAttributesCard";
import { SystemHeaderCard } from "@/components/cards/SystemHeader";
import { WaiversCard } from "@/components/cards/WaiversCard";
import { WellsCard } from "@/components/cards/WellsCard";
import { useAuth } from "@/hooks/useAuth";
import { usePageTitle } from "@/hooks/usePageTitle";

const REFETCH_INTERVAL_MS = 15 * 60 * 1000;
const DOCUMENTS_REFETCH_MS = 60 * 60 * 1000; // 1h client-side; backend caches 3h

export function FacilityDashboard(): JSX.Element {
  const { pwsId = "" } = useParams();
  const queryClient = useQueryClient();
  const auth = useAuth();
  const canRefresh = auth.role === "admin" || auth.role === "operator";

  const query = useQuery({
    queryKey: ["system", pwsId],
    queryFn: () => fetchSystem(pwsId),
    refetchInterval: REFETCH_INTERVAL_MS,
    enabled: pwsId.length > 0,
  });

  const refresh = useMutation({
    mutationFn: () => refreshSystem(pwsId),
    onSuccess: (data) => {
      queryClient.setQueryData(["system", pwsId], data);
    },
  });

  const docsQuery = useQuery({
    queryKey: ["system-documents", pwsId],
    queryFn: () => fetchSystemDocuments(pwsId),
    refetchInterval: DOCUMENTS_REFETCH_MS,
    enabled: pwsId.length > 0,
  });

  const docsRefresh = useMutation({
    mutationFn: () => refreshSystemDocuments(pwsId),
    onSuccess: (data) => {
      queryClient.setQueryData(["system-documents", pwsId], data);
    },
  });

  const nnQuery = useQuery({
    queryKey: ["compliance-nn", pwsId],
    queryFn: () => fetchNitrateNitriteCompliance(pwsId),
    refetchInterval: DOCUMENTS_REFETCH_MS,
    enabled: pwsId.length > 0,
  });

  usePageTitle(query.data?.header.pws_name ?? `PWS ${pwsId}`);

  if (query.isLoading) {
    return <FacilitySkeleton />;
  }

  if (query.error) {
    const err = query.error;
    const msg = err instanceof ApiError ? err.message : "Failed to load system";
    return (
      <div className="space-y-4">
        <CardError title="Facility" message={msg} />
      </div>
    );
  }

  const data = query.data!;

  return (
    <div className="space-y-4">
      <nav aria-label="Breadcrumb" className="text-sm text-ink-400">
        <Link to="/systems" className="hover:text-ink-100">
          Systems
        </Link>
        <span className="mx-1.5" aria-hidden="true">
          ›
        </span>
        <span className="text-ink-200">{data.header.pws_name}</span>
      </nav>
      <p className="text-sm text-ink-300">
        Everything about this system in one place: issues to act on at the top,
        then the testing schedule, the facts that drive which rules apply, the
        equipment, and the documents on file with FDEP. New to this?{" "}
        <Link to="/getting-started" className="text-ink-100 underline hover:no-underline">
          Read the getting-started guide
        </Link>
        .
      </p>
      <AlertsBanner alerts={data.alerts} />
      <SystemHeaderCard
        header={data.header}
        fetchedAt={data.fetched_at}
        stale={data.stale}
        canRefresh={canRefresh}
        onRefresh={() => refresh.mutate()}
        refreshing={refresh.isPending}
        refreshError={refresh.isError}
      />
      <NitrateNitriteCard
        status={nnQuery.data}
        loading={nnQuery.isLoading}
        error={nnQuery.isError}
      />
      <MonitoringScheduleCard pwsId={pwsId} />
      <SystemAttributesCard pwsId={pwsId} />
      <WaiversCard pwsId={pwsId} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PlantCard plants={data.plants} />
        <WellsCard wells={data.wells} />
        <StorageCard tanks={data.tanks} designCapacityGpd={data.header.design_capacity_gpd} />
        <LSLICard lsli={data.lsli} />
      </div>
      {docsQuery.error ? (
        <CardError
          title="FDEP documents"
          message={
            docsQuery.error instanceof ApiError
              ? docsQuery.error.message
              : "Failed to load documents"
          }
        />
      ) : (
        <DocumentsCard
          documents={docsQuery.data?.documents ?? []}
          fetchedAt={docsQuery.data?.fetched_at ?? new Date().toISOString()}
          stale={docsQuery.data?.stale ?? false}
          loading={docsQuery.isLoading}
          refreshing={docsRefresh.isPending}
          canRefresh={canRefresh}
          onRefresh={() => docsRefresh.mutate()}
          refreshError={docsRefresh.isError}
        />
      )}
    </div>
  );
}

function FacilitySkeleton(): JSX.Element {
  return (
    <div className="space-y-4">
      <CardSkeleton title="Alerts" rows={2} />
      <CardSkeleton title="Header" rows={2} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CardSkeleton title="Plants" />
        <CardSkeleton title="Wells" />
        <CardSkeleton title="Storage" />
        <CardSkeleton title="LSLI" />
      </div>
    </div>
  );
}
